import { useEffect, useMemo, useState } from 'react'
import { Alert, Card, Col, DatePicker, Row, Select, Space, Statistic, Switch, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Dayjs } from 'dayjs'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { rangeToIso, tenantToday } from '@/shared/lib/dayRange'
import { applyLocalList, byNumber, byText, localSortChange, sortColumn, useLocalListState } from '@/shared/lib/localList'
import { formatMoneyValue } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { renderSummary, type SummaryLine } from '@/shared/ui/table/tableSummary'
import { fetchCashFlow, type CashFlowFigures, type CashFlowReport } from '@/features/cashdesk/cashdeskApi'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { useCashBranches } from '@/features/cashdesk/useCashBranches'

type FlowKey = keyof CashFlowFigures
type BranchRow = CashFlowReport['branches'][number]
type MonthRow = CashFlowReport['months'][number]

const FLOW_KEYS: readonly FlowKey[] = ['opening', 'income', 'received', 'expenses', 'transferred', 'other', 'closing']

const idle = (row: CashFlowFigures) => FLOW_KEYS.every((key) => row[key].amount === 0)

/** Bosh ofis moliyachisi uchun: har filial kassasi davr bo'yicha va oyma-oy, jami bilan */
export function FinanceBranchCashPage() {
  const { t } = useTranslation()
  const branches = useCashBranches()
  const { selected: activeBranches } = useActiveBranches()
  const [picked, setPicked] = useState<string[]>(() => [...activeBranches])
  const activeKey = activeBranches.join()
  useEffect(() => {
    setPicked(activeKey ? activeKey.split(',') : [])
  }, [activeKey])
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(() => [tenantToday().startOf('month'), tenantToday()])
  const span = useMemo(() => (range ? rangeToIso(range) : null), [range])
  const list = useLocalListState({ prefix: 'f' })
  const [showIdle, setShowIdle] = useState(false)

  const report = useQuery({
    queryKey: queryKeys.cashdesk.flow(picked, span?.from ?? null, span?.to ?? null),
    queryFn: ({ signal }) => fetchCashFlow(picked, span?.from ?? null, span?.to ?? null, signal),
    enabled: !branches.unknown,
  })

  const branchRows = useMemo(
    () =>
      applyLocalList((report.data?.branches ?? []).filter((row) => showIdle || !idle(row)), {
        search: '',
        filterState: {},
        comparators: {
          name: byText((row: BranchRow) => row.name),
          ...Object.fromEntries(FLOW_KEYS.map((key) => [key, byNumber((row: BranchRow) => row[key].amount)])),
        },
        sort: list.sort,
      }),
    [report.data, list.sort, showIdle],
  )
  const idleCount = (report.data?.branches ?? []).filter(idle).length

  const branchColumns = useMemo<ColumnsType<BranchRow>>(
    () => [
      {
        key: 'name',
        title: t('cashdesk.columns.branch'),
        fixed: 'left',
        width: 200,
        ...sortColumn(list.sort, 'name'),
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <span>{row.name}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.code}
            </Typography.Text>
          </Space>
        ),
      },
      ...FLOW_KEYS.map((key) => ({
        key,
        title: t(`cashFlow.columns.${key}`),
        width: 128,
        align: 'right' as const,
        ...sortColumn(list.sort, key),
        render: (_: unknown, row: BranchRow) => formatMoneyValue(row[key]),
      })),
    ],
    [t, list.sort],
  )

  const monthColumns = useMemo<ColumnsType<MonthRow>>(
    () => [
      { key: 'month', title: t('cashFlow.columns.month'), width: 120, render: (_, row) => row.month.slice(0, 7) },
      ...FLOW_KEYS.map((key) => ({
        key,
        title: t(`cashFlow.columns.${key}`),
        width: 128,
        align: 'right' as const,
        render: (_: unknown, row: MonthRow) => formatMoneyValue(row[key]),
      })),
    ],
    [t],
  )

  const total = report.data?.total ?? null
  const totals = (labelColumn: string): SummaryLine[] =>
    total
      ? [
          {
            key: 'total',
            cells: {
              [labelColumn]: <Typography.Text strong>{t('common.total')}</Typography.Text>,
              ...Object.fromEntries(
                FLOW_KEYS.map((key) => [key, <Typography.Text strong>{formatMoneyValue(total[key])}</Typography.Text>]),
              ),
            },
          },
        ]
      : []

  return (
    <>
      <PageHeader title={t('nav.financeBranchCash')} description={t('cashFlow.intro')} />

      <Space wrap style={{ marginBottom: 16 }}>
        <Select<string[]>
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          style={{ minWidth: 260 }}
          placeholder={t('cashdesk.period.allBranches')}
          value={picked}
          onChange={setPicked}
          optionFilterProp="label"
          options={branches.all.map((b) => ({ value: b.id, label: `${b.name} (${b.code})` }))}
        />
        <DatePicker.RangePicker
          allowClear
          value={range}
          placeholder={[t('cashdesk.period.allTime'), t('cashdesk.period.allTime')]}
          disabledDate={(day) => day.isAfter(tenantToday(), 'day')}
          presets={[
            { label: t('cashFlow.presets.thisMonth'), value: [tenantToday().startOf('month'), tenantToday()] },
            {
              label: t('cashFlow.presets.lastMonth'),
              value: [
                tenantToday().subtract(1, 'month').startOf('month'),
                tenantToday().subtract(1, 'month').endOf('month'),
              ],
            },
            { label: t('cashFlow.presets.thisYear'), value: [tenantToday().startOf('year'), tenantToday()] },
          ]}
          onChange={(value) => setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)}
        />
      </Space>

      {branches.error ? <Alert type="error" showIcon message={translateError(branches.error, t)} /> : null}
      {report.error ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(report.error, t)} />
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {(['income', 'expenses', 'transferred', 'closing'] as const).map((key) => (
          <Col key={key} xs={12} lg={6}>
            <Card size="small" loading={report.isPending}>
              <Statistic title={t(`cashFlow.kpi.${key}`)} value={total ? formatMoneyValue(total[key]) : '—'} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title={t('cashFlow.byBranch')}
        style={{ marginBottom: 16 }}
        extra={
          idleCount > 0 ? (
            <Space>
              <Typography.Text type="secondary">{t('cashFlow.idle', { count: idleCount })}</Typography.Text>
              <Switch size="small" checked={showIdle} onChange={setShowIdle} />
            </Space>
          ) : null
        }
      >
        <Table<BranchRow>
          size="small"
          rowKey={(row) => row.id}
          columns={branchColumns}
          dataSource={[...branchRows]}
          loading={branches.isPending || report.isPending}
          pagination={false}
          scroll={{ x: 1100 }}
          onChange={localSortChange<BranchRow>(list.patch)}
          summary={() => renderSummary(branchColumns, totals('name'))}
          locale={{ emptyText: t('cashdesk.branches.empty') }}
        />
      </Card>

      <Card title={t('cashFlow.byMonth')}>
        <Table<MonthRow>
          size="small"
          rowKey={(row) => row.month}
          columns={monthColumns}
          dataSource={[...(report.data?.months ?? [])]}
          loading={report.isPending}
          pagination={false}
          scroll={{ x: 1020 }}
          summary={() => renderSummary(monthColumns, totals('month'))}
          locale={{ emptyText: t('cashFlow.noMovement') }}
        />
      </Card>
    </>
  )
}
